import React, {
  useCallback, useEffect, useState, useMemo, MutableRefObject,
} from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';

import {
  selectBookmarkAction,
  selectBookmarkPosition,
  selectFontSize,
  selectGuide,
  selectHomeAction,
  selectIsPlayPrompter,
  selectMainContainerHeight,
  selectText,
} from '../../../store/modules/studio/selectors';
import {
  setBookmarkAction,
  setHomeAction,
  setRestartPrompter,
} from '../../../store/modules/studio/actions';
import { PrompterConfig } from '../utils';
import { PromptTextStyles } from '../styles';
import { GuideValues } from '../../../configs/studio';

type Props = {
  refPrompter: MutableRefObject<HTMLDivElement | null>;
  requestPrompterPosition: (e: React.MouseEvent | KeyboardEvent | null, top: number) => void;
};

const PromptText = (
  { refPrompter, requestPrompterPosition }: Props,
) => {
  const dispatch = useDispatch();

  const classes = PromptTextStyles();
  const [startPosition, setStartPosition] = useState(0);
  const height = useSelector(selectMainContainerHeight);
  const currentFontSize = useSelector(selectFontSize);
  const guide = useSelector(selectGuide);
  const currentText = useSelector(selectText);
  const isActiveHomeAction = useSelector(selectHomeAction);
  const isActiveBookmarkAction = useSelector(selectBookmarkAction);
  const bookmarkPosition = useSelector(selectBookmarkPosition);
  const paddingHeight = currentFontSize * 1.5;
  const highlightHeight = currentFontSize * PrompterConfig.highlightGuide;
  const isPlay = useSelector(selectIsPlayPrompter);

  useEffect(() => {
    const preventEvent = (e: TouchEvent | WheelEvent) => {
      e.preventDefault();
    };
    const promptContainer = refPrompter.current;

    if (promptContainer) {
      promptContainer.addEventListener('touchstart', preventEvent);
      promptContainer.addEventListener('touchmove', preventEvent);
      promptContainer.addEventListener('wheel', preventEvent);
    }

    return () => {
      if (promptContainer) {
        promptContainer.removeEventListener('touchstart', preventEvent);
        promptContainer.removeEventListener('touchmove', preventEvent);
        promptContainer.removeEventListener('wheel', preventEvent);
      }
    };
  }, [refPrompter]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setStartPosition(e.touches[0].pageY);
  }, [setStartPosition]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    if (refPrompter.current) {
      const top = refPrompter.current.scrollTop + e.deltaY;
      requestPrompterPosition(null, top);
      if (isPlay) {
        dispatch(setRestartPrompter());
      }
    }
  }, [refPrompter, requestPrompterPosition, dispatch, isPlay]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (refPrompter.current) {
      const endPosition = e.touches[0].pageY;
      const top = refPrompter.current.scrollTop + (startPosition - endPosition) * 2;
      setStartPosition(endPosition);
      requestPrompterPosition(null, top);
      if (isPlay) {
        dispatch(setRestartPrompter());
      }
    }
  }, [refPrompter, startPosition, setStartPosition, requestPrompterPosition, dispatch, isPlay]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    (async () => {
      const top = (e.target as HTMLDivElement).scrollTop;
      if (top <= 0) {
        dispatch(setHomeAction(true));
      } else if (top > 0 && isActiveHomeAction) {
        dispatch(setHomeAction(false));
      }
      if (bookmarkPosition !== null) {
        if ((top >= bookmarkPosition) && (bookmarkPosition + currentFontSize >= top)) {
          dispatch(setBookmarkAction(true));
        } else if (isActiveBookmarkAction) {
          dispatch(setBookmarkAction(false));
        }
      }
    })();
  }, [dispatch, isActiveHomeAction, isActiveBookmarkAction, currentFontSize, bookmarkPosition]);

  const guidHeight = useMemo(() => ((height < highlightHeight) ? height : highlightHeight), [height, highlightHeight]);

  const getTop = useMemo(() => {
    let top = 0;
    switch (guide) {
      case GuideValues.Center:
        top = (height / 2) - (guidHeight / 2) + 10;
        break;
      case GuideValues.Bottom:
        top = height - (guidHeight) - 10 - 10;
        break;
      default:
        top = 10;
    }

    top = top < 10 ? 10 : top;

    return top;
  }, [height, guide, guidHeight]);

  const getTopPadding = useMemo(() => {
    switch (guide) {
      case GuideValues.Center:
        return paddingHeight + (height / 2) - (guidHeight / 2);
      case GuideValues.Bottom:
        return paddingHeight + height - guidHeight - 10 - 10 - 10;
      default:
        return paddingHeight;
    }
  }, [guide, paddingHeight, height, guidHeight]);

  const guideMaxHeight = useMemo(() => {
    switch (guide) {
      case GuideValues.Center:
        return guidHeight - 20 * 2;
      case GuideValues.Bottom:
        return guidHeight - 20 * 2;
      default:
        return guidHeight - 20 * 2;
    }
  }, [guide, guidHeight]);

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onWheel={onWheel}
      onScroll={onScroll}
      ref={refPrompter}
      className={clsx(classes.container, classes.noselect)}
      style={{
        fontSize: currentFontSize,
        paddingTop: getTopPadding,
        height: '100%',
      }}
    >
      <div className={classes.guideContainer}>
        <div
          hidden={guide === GuideValues.Off}
          className={classes.textHighlightBar}
          style={{
            height: guidHeight,
            maxHeight: guideMaxHeight,
            top: getTop,
          }}
        />
      </div>
      {ReactHtmlParser(currentText)}
      <div style={{ height }} />
    </div>
  );
};


export default PromptText;
